import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
import { useRelay } from "../hub/relay-provider.js";
import { setSessionId, setViewActive, setViewInactive, subscribe, } from "../scout/peer-manager.js";
import { ChannelView } from "./ChannelView.js";
const DEFAULT_CHAT_WIDTH = 340;
const CHAT_WIDTH_STORAGE_KEY = "kritaka:web:scoutChatWidth";
const MIN_CHAT_WIDTH = 240;
const MAX_CHAT_WIDTH = 720;
/**
 * Scout stream viewer, ported from kritaka-electron/src/renderer/components/
 * scouts/ScoutStreamView.tsx. Renders the remote MediaStream in the left
 * pane and the docked stream-channel chat on the right, with the KTK-184
 * ephemeral scout_progress row inline at the bottom of the chat flow.
 *
 * The peer-manager (WebRTC lifecycle) + relay-client (signal fanout) are
 * the two browser-side pieces that replace kritaka-electron's main-process
 * channel-hub. All viewer_interest / stop_goal writes go through the
 * injected `HubClient`.
 */
export function ScoutView({ instanceId, streamChannel, workspaceId, authorName, onLeaseReleased, fallbackDisplayName }) {
    const ctx = useDataContext();
    const scoutId = instanceId;
    const scoutChannelId = streamChannel?.id ?? null;
    const [scout, setScout] = useState(null);
    const relay = useRelay();
    // --- Load scout details + poll for lease-release detection ---
    useEffect(() => {
        if (!workspaceId)
            return;
        let cancelled = false;
        const load = async () => {
            try {
                const scouts = await ctx.hub.listWorkspaceScouts(workspaceId);
                if (cancelled)
                    return;
                const match = scouts.find((s) => s.id === scoutId) ?? null;
                setScout(match);
                if (match && !match.leased_at) {
                    setToast("Lease released");
                    window.setTimeout(() => {
                        if (!cancelled)
                            setToast(null);
                        onLeaseReleased?.();
                    }, 2000);
                }
                else if (!match) {
                    setToast("Lease released");
                    window.setTimeout(() => {
                        if (!cancelled)
                            setToast(null);
                        onLeaseReleased?.();
                    }, 2000);
                }
            }
            catch {
                /* non-fatal */
            }
        };
        void load();
        const interval = window.setInterval(() => { void load(); }, 15_000);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [ctx.hub, workspaceId, scoutId, onLeaseReleased]);
    const [peerState, setPeerState] = useState({
        mediaStream: null,
        sessionId: null,
        error: null,
        connecting: false,
    });
    const [chatCollapsed, setChatCollapsed] = useState(false);
    const [chatWidth, setChatWidth] = useState(() => {
        try {
            const raw = localStorage.getItem(CHAT_WIDTH_STORAGE_KEY);
            const parsed = raw ? Number.parseInt(raw, 10) : NaN;
            return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_CHAT_WIDTH;
        }
        catch {
            return DEFAULT_CHAT_WIDTH;
        }
    });
    const [draggingChatWidth, setDraggingChatWidth] = useState(false);
    useEffect(() => {
        if (!draggingChatWidth)
            return;
        const onMove = (e) => {
            setChatWidth((prev) => {
                const next = Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, prev - e.movementX));
                try {
                    localStorage.setItem(CHAT_WIDTH_STORAGE_KEY, String(next));
                }
                catch {
                    /* quota / private browsing — width still applies for this session */
                }
                return next;
            });
        };
        const onUp = () => setDraggingChatWidth(false);
        const prevSelect = document.body.style.userSelect;
        const prevCursor = document.body.style.cursor;
        document.body.style.userSelect = "none";
        document.body.style.cursor = "col-resize";
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.userSelect = prevSelect;
            document.body.style.cursor = prevCursor;
        };
    }, [draggingChatWidth]);
    const [watchers, setWatchers] = useState([]);
    const [toast, setToast] = useState(null);
    const [progress, setProgress] = useState(null);
    const videoRef = useRef(null);
    // --- Peer subscription + view lifecycle ---
    useEffect(() => {
        setViewActive(scoutId);
        const unsubscribe = subscribe(scoutId, setPeerState);
        return () => {
            unsubscribe();
            setViewInactive(scoutId);
        };
    }, [scoutId]);
    useEffect(() => {
        const el = videoRef.current;
        if (!el)
            return;
        if (el.srcObject !== peerState.mediaStream)
            el.srcObject = peerState.mediaStream;
    }, [peerState.mediaStream]);
    // --- Presence join/leave ---
    useEffect(() => {
        void ctx.hub.joinScoutPresence(scoutId).catch(() => { });
        return () => {
            void ctx.hub.leaveScoutPresence(scoutId).catch(() => { });
        };
    }, [ctx.hub, scoutId]);
    // --- Watchers list ---
    useEffect(() => {
        if (!scoutChannelId) {
            setWatchers([]);
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                const subs = await ctx.hub.getChannelSubscribers(workspaceId, scoutChannelId);
                if (!cancelled)
                    setWatchers(subs);
            }
            catch {
                /* ignore; stays empty */
            }
        };
        void load();
        // Refresh every 20s — covers presence_delta gaps when the relay WSS
        // isn't yet fanning those out to web clients (legacy paths).
        const interval = window.setInterval(() => { void load(); }, 20_000);
        return () => {
            cancelled = true;
            window.clearInterval(interval);
        };
    }, [ctx.hub, workspaceId, scoutChannelId]);
    // --- Relay WSS subscription for signal fanout (scout_progress,
    // stream_active/inactive). Uses the RelayProvider's shared client so the
    // WSS connection is shared with chat live-sync + unread badge plumbing. ---
    useEffect(() => {
        const unsubscribe = relay.onMessage((frame) => {
            if (frame.type !== "signal")
                return;
            const channelId = typeof frame.channel_id === "string" ? frame.channel_id : null;
            if (channelId && channelId !== scoutChannelId)
                return;
            const kind = typeof frame.kind === "string" ? frame.kind : null;
            const payload = frame.payload ?? {};
            switch (kind) {
                case "scout_progress": {
                    const done = payload.done === true;
                    if (done) {
                        setProgress(null);
                        return;
                    }
                    const goalId = typeof payload.goal_id === "string" ? payload.goal_id : null;
                    const action = typeof payload.action === "string" ? payload.action : "";
                    setProgress({ goalId, action });
                    return;
                }
                case "stream_active": {
                    const sid = typeof payload.session_id === "string" ? payload.session_id : null;
                    setSessionId(scoutId, sid);
                    return;
                }
                case "stream_inactive": {
                    setSessionId(scoutId, null);
                    return;
                }
            }
        });
        return unsubscribe;
    }, [relay, scoutId, scoutChannelId]);
    // --- Progress auto-clear on 10s idle ---
    useEffect(() => {
        if (!progress)
            return;
        const t = window.setTimeout(() => setProgress(null), 10_000);
        return () => window.clearTimeout(t);
    }, [progress]);
    const handleStopGoal = () => {
        if (!progress?.goalId)
            return;
        void ctx.hub.postScoutStopGoal(scoutId, progress.goalId).catch(() => { });
        setProgress(null);
    };
    const platformLabel = useMemo(() => {
        if (!scout)
            return "";
        return scout.platform === "macos" ? "macOS" : scout.platform === "windows" ? "Windows" : scout.platform;
    }, [scout]);
    const displayName = scout?.display_name ?? fallbackDisplayName ?? "Scout";
    return (_jsxs("div", { style: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            background: "var(--bg-primary)",
            minWidth: 0,
            overflow: "hidden",
            position: "relative",
        }, children: [_jsxs("div", { style: {
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 24px",
                    borderBottom: "1px solid var(--border)",
                    flexShrink: 0,
                    gap: 12,
                }, children: [_jsx("span", { style: { fontWeight: 600, fontSize: 14 }, children: displayName }), platformLabel && (_jsx("span", { style: {
                            fontSize: 11,
                            color: "var(--text-muted)",
                            borderLeft: "1px solid var(--border)",
                            paddingLeft: 12,
                        }, children: platformLabel })), _jsxs("div", { style: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }, children: [_jsx(WhosWatching, { watchers: watchers }), _jsx("button", { onClick: () => setChatCollapsed((c) => !c), title: chatCollapsed ? "Show chat" : "Hide chat", style: {
                                    background: "none",
                                    border: "1px solid var(--border)",
                                    borderRadius: 4,
                                    color: "var(--text-muted)",
                                    cursor: "pointer",
                                    padding: "4px 10px",
                                    fontSize: 11,
                                }, children: chatCollapsed ? "Chat" : "Hide chat" })] })] }), _jsxs("div", { style: { flex: 1, display: "flex", minHeight: 0 }, children: [_jsx(StreamSurface, { scout: scout, displayName: displayName, peerState: peerState, videoRef: videoRef }), !chatCollapsed && (_jsx("div", { onMouseDown: (e) => {
                            e.preventDefault();
                            setDraggingChatWidth(true);
                        }, title: "Drag to resize chat", style: {
                            width: 4,
                            flexShrink: 0,
                            cursor: "col-resize",
                            background: draggingChatWidth ? "var(--accent)" : "var(--border)",
                            transition: draggingChatWidth ? "none" : "background 0.15s",
                        }, onMouseEnter: (e) => {
                            if (!draggingChatWidth)
                                e.currentTarget.style.background = "var(--accent)";
                        }, onMouseLeave: (e) => {
                            if (!draggingChatWidth)
                                e.currentTarget.style.background = "var(--border)";
                        } })), !chatCollapsed && (_jsx("div", { style: {
                            width: chatWidth,
                            flexShrink: 0,
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                            padding: "0 12px",
                        }, children: scoutChannelId && streamChannel ? (_jsx(ChannelView, { workspaceId: workspaceId, channelId: scoutChannelId, channelName: streamChannel.name, channelDescription: streamChannel.description ?? undefined, authorName: authorName, messagesFooter: progress ? (_jsx(ProgressRow, { action: progress.action, onStop: handleStopGoal, canStop: !!progress.goalId })) : null })) : (_jsx("div", { style: {
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 16,
                                textAlign: "center",
                                fontSize: 12,
                                color: "var(--text-muted)",
                            }, children: "Stream channel is not available yet. It's auto-created when Scout connects to the relay." })) }))] }), toast && (_jsx("div", { style: {
                    position: "absolute",
                    bottom: 24,
                    left: "50%",
                    transform: "translateX(-50%)",
                    padding: "8px 16px",
                    borderRadius: 6,
                    fontSize: 13,
                    background: "var(--bg-secondary)",
                    border: "1px solid var(--border)",
                    color: "var(--text-primary)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }, children: toast }))] }));
}
function StreamSurface({ scout, displayName, peerState, videoRef }) {
    if (!peerState.mediaStream) {
        const isStale = scout ? !scout.leased_at : true;
        const body = peerState.error
            ? peerState.error
            : isStale
                ? "Waiting for lease to take effect."
                : peerState.sessionId
                    ? "Negotiating WebRTC session…"
                    : "Desktop stream will appear here when Scout connects.";
        return (_jsx("div", { style: {
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#0a0b0d",
                color: "var(--text-muted)",
                minWidth: 0,
            }, children: _jsxs("div", { style: { textAlign: "center", padding: 24 }, children: [_jsxs("div", { style: { fontSize: 14, color: "var(--text-secondary)", marginBottom: 6 }, children: [displayName, " ", peerState.sessionId ? "connecting…" : "is offline"] }), _jsx("div", { style: { fontSize: 12, color: "var(--text-muted)" }, children: body })] }) }));
    }
    return (_jsx("div", { style: { flex: 1, minWidth: 0, position: "relative", background: "#000" }, children: _jsx("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, style: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" } }) }));
}
function WhosWatching({ watchers }) {
    const [expanded, setExpanded] = useState(false);
    const count = watchers.length;
    return (_jsxs("div", { style: { position: "relative" }, onMouseEnter: () => setExpanded(true), onMouseLeave: () => setExpanded(false), children: [_jsxs("div", { style: {
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 10px",
                    borderRadius: 12,
                    border: "1px solid var(--border)",
                    background: "var(--bg-secondary)",
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    cursor: "default",
                }, children: [_jsx("span", { style: {
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: count > 0 ? "var(--success)" : "var(--text-muted)",
                        } }), count > 0 ? `${count} watching` : "No one watching"] }), expanded && count > 0 && (_jsx("div", { style: {
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    right: 0,
                    zIndex: 100,
                    background: "var(--bg-tertiary)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    padding: "6px 0",
                    minWidth: 160,
                    maxHeight: 300,
                    overflowY: "auto",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                }, children: watchers.map((a) => (_jsxs("div", { style: {
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "4px 12px",
                        fontSize: 12,
                        color: "var(--text-primary)",
                    }, children: [_jsx("span", { style: {
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: a.status === "running" ? "var(--success)" : "var(--text-muted)",
                                flexShrink: 0,
                            } }), _jsx("span", { children: a.name })] }, a.id))) }))] }));
}
function ProgressRow({ action, onStop, canStop }) {
    return (_jsxs("div", { style: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            margin: "4px -12px",
            borderRadius: 6,
            fontSize: 12,
            color: "var(--text-secondary)",
        }, children: [_jsx("span", { style: {
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "var(--success, #3fb950)",
                    animation: "scout-progress-pulse 1s ease-in-out infinite",
                    flexShrink: 0,
                } }), _jsx("span", { style: {
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontStyle: "italic",
                }, children: action || "Working…" }), canStop && (_jsx("button", { onClick: onStop, title: "Stop goal execution", style: {
                    background: "none",
                    border: "1px solid var(--border)",
                    borderRadius: 4,
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    padding: "2px 8px",
                    fontSize: 11,
                }, children: "Stop" })), _jsx("style", { children: `
        @keyframes scout-progress-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      ` })] }));
}
//# sourceMappingURL=ScoutView.js.map